import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import {
  Payer,
  PayerEngagement,
  PayerEngagementStatus,
  PayerRowsApiSearchPayerRowsRequest
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useMemo, useState } from "react";
import { queriesPayerEngagements, queriesPayerRows } from "@query";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { useAddClientModal } from "../AddClientModal/useAddClientModal";

type Props = Omit<WSSelectSearchProps, "options"> & {
  defaultSearchText?: string;
  onAddNewPayer: (payer: Payer, engagements: PayerEngagement[]) => void;
  baseRequest?: PayerRowsApiSearchPayerRowsRequest;
  menuFooterText?: string;
  hideMenuFooterAction?: boolean;
  customMenuFooterAction?: WSSelectSearchProps["menuFooterAction"];
};

export const SelectPayerEngagement: React.FC<Props> = ({
  defaultSearchText,
  onAddNewPayer,
  baseRequest,
  menuFooterText,
  hideMenuFooterAction,
  customMenuFooterAction,
  ...otherProps
}) => {
  const [searchText, setSearchText] = useState(defaultSearchText);
  const queryPayerRows = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    ...baseRequest,
    filter: {
      ...baseRequest?.filter,
      searchString: searchText ? { eq: searchText } : undefined
    }
  });

  const onReachMenuEnd = useCallback(() => {
    if (queryPayerRows.hasNextPage) {
      queryPayerRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayerRows.hasNextPage]);

  const modalAddClient = useAddClientModal();

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    const workLoggingFilter =
      baseRequest?.filter?.engagements_isWorkLoggingEnabled?.eq;

    (queryPayerRows.flatData ?? []).forEach(payer => {
      const name = selectorSearchRowName(payer);

      // Active, non-duplicate engagements are selectable. The v1 `labels.duplicate`
      // flag is carried on the v3 row engagement's `metadata` map (v1 `labels`
      // → v3 `metadata`); metadata values are strings, so the boolean `true`
      // becomes the string `"true"`.
      let filteredEngagements = (payer.engagements ?? []).filter(
        e =>
          e.status === PayerEngagementStatus.ACTIVATED &&
          e.metadata?.duplicate !== "true"
      );

      if (workLoggingFilter !== undefined) {
        filteredEngagements = filteredEngagements.filter(
          e => !!e.isWorkLoggingEnabled === workLoggingFilter
        );
      }

      filteredEngagements.forEach(e => {
        options.push({
          value: payer.id + "/" + e.payerEngagementId,
          // Default engagements have no distinct name to show alongside the payer.
          label: [name, e.isDefault ? undefined : e.engagementName]
            .filter(Boolean)
            .join(" • "),
          labels: payer.email ? [payer.email] : [],
          avatar: {
            type: "text",
            text: name
          }
        });
      });
    });

    return options;
  }, [queryPayerRows.flatData, baseRequest?.filter]);

  return (
    <WSSelectSearch
      {...otherProps}
      // label="Payer"
      placeholder="Select an option"
      options={options}
      status={queryPayerRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        queryPayerRows.isFetching && !queryPayerRows.isFetchingNextPage
      }
      onReachMenuEnd={onReachMenuEnd}
      menuFooterText={
        menuFooterText ??
        "Don't see a Client name? Add a new client to your client list."
      }
      menuFooterAction={
        hideMenuFooterAction
          ? undefined
          : (customMenuFooterAction ?? {
              label: "Add client",
              onClick: async () => {
                const payer = await modalAddClient.open();
                if (payer) {
                  const engagements =
                    await queriesPayerEngagements.listPayerEngagements.fetchQueryData(
                      { payerId: payer.id! }
                    );
                  onAddNewPayer(payer, engagements.data ?? []);
                }
              }
            })
      }
    />
  );
};
